import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Award, Heart } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const QuemSomosPage = () => {
  const navigateTo = (url) => {
    window.location.href = url;
  };

  const values = [
    {
      icon: Target,
      title: 'Nossa Missão',
      description: 'Simplificar a vida do MEI brasileiro, oferecendo soluções práticas e acessíveis para gestão do seu negócio.'
    },
    {
      icon: Users,
      title: 'Nossa Equipe',
      description: 'Profissionais especializados e dedicados a ajudar microempreendedores a prosperarem em seus negócios.'
    },
    {
      icon: Award,
      title: 'Nosso Compromisso',
      description: 'Excelência no atendimento e qualidade garantida, comprovada pelo selo RA1000 no Reclame Aqui.'
    },
    {
      icon: Heart,
      title: 'Nossos Valores',
      description: 'Transparência, agilidade e compromisso com o sucesso dos nossos clientes em cada etapa.'
    }
  ];

  return (
    <div className="min-h-screen font-sans">
      {/* Header padronizado */}
      <Header />

      {/* Hero Section */}
      <section 
        className="relative py-32 md:py-40 px-4 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
        }}
      >
        {/* Elementos decorativos de fundo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
          <div 
            className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #ffffff 0%, transparent 70%)' }}
          />
          {/* Padrão de pontos */}
          <div 
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
              backgroundSize: '30px 30px',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6"
            >
              Conheça o MeuMei
            </motion.span>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Quem Somos
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed opacity-90">
              Somos a plataforma líder em soluções para MEI no Brasil, dedicada a transformar a gestão do seu negócio.
            </p>
          </motion.div>
        </div>

        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            {/* Imagem */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <img 
                  src="https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564438/Leonardo_Kino_XL_create_an_image_where_an_older_white_man_is_l_0_tmiu6i.jpg"
                  alt="Equipe MeuMei"
                  className="w-full h-[400px] object-cover"
                />
                <div 
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, transparent 100%)',
                  }}
                />
              </motion.div>
              {/* Badge decorativo */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="absolute -bottom-6 -right-6 bg-[#10B981] text-white p-6 rounded-2xl shadow-xl"
              >
                <div className="text-3xl font-bold">5+</div>
                <div className="text-sm opacity-90">Anos de experiência</div>
              </motion.div>
            </div>

            {/* Texto */}
            <div>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-[#10B981] font-semibold text-sm uppercase tracking-wider"
              >
                Nossa História
              </motion.span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2C5E50] mt-2 mb-6">
                Nascemos para facilitar a vida do MEI
              </h2>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  O MeuMei nasceu para facilitar a vida do Microempreendedor Individual e contribuir, de forma real, para o crescimento da sua empresa. Sabemos que empreender não é simples, por isso criamos uma plataforma moderna, intuitiva e preparada para o dia a dia de quem está sempre correndo atrás.
                </p>
                <p>
                  Aqui, você encontra tudo em um único lugar: organização de documentos, emissão de certidões, controle financeiro, ferramentas de gestão e soluções que tornam sua rotina mais leve, clara e eficiente.
                </p>
                <p>
                  Acreditamos que, quando o MEI está organizado, ele vende mais, toma decisões melhores e evolui com muito mais segurança.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#10B981] font-semibold text-sm uppercase tracking-wider">
              O que nos move
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C5E50] mt-2">
              Nossos Pilares
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <value.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#2C5E50] mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        className="py-20 px-4 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #2C5E50 0%, #1a3d35 100%)',
        }}
      >
        {/* Elementos decorativos */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-3 gap-8 text-center text-white">
            {[
              { number: '10.000+', label: 'MEIs Atendidos' },
              { number: '98%', label: 'Satisfação' },
              { number: 'RA1000', label: 'Selo Reclame Aqui' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="p-8"
              >
                <div 
                  className="text-5xl md:text-6xl font-bold mb-3"
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #6ee7b7 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {stat.number}
                </div>
                <div className="text-xl opacity-80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center bg-gradient-to-br from-gray-50 to-green-50 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{
              boxShadow: '0 20px 60px rgba(16, 185, 129, 0.15)',
            }}
          >
            {/* Decoração */}
            <div 
              className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-30 pointer-events-none"
              style={{
                background: 'radial-gradient(circle, #10B981 0%, transparent 70%)',
                transform: 'translate(30%, -30%)',
              }}
            />

            <h2 className="text-3xl md:text-4xl font-bold text-[#2C5E50] mb-6 relative z-10">
              Pronto para Começar?
            </h2>
            <p className="text-xl text-gray-600 mb-8 relative z-10">
              Junte-se a milhares de MEIs que já confiam na nossa plataforma.
            </p>
            <motion.button 
              onClick={() => navigateTo('https://app.meumei.net/register')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative px-10 py-4 text-white font-semibold text-lg rounded-full overflow-hidden group"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
              }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.6 }}
              />
              <span className="relative z-10 flex items-center gap-2 justify-center">
                Abrir meu CNPJ MEI
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="group-hover:translate-x-1 transition-transform"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer padronizado */}
      <Footer />
    </div>
  );
};

export default QuemSomosPage;